/**
 * Electrical Equipment Tool - BIM 2025 Grade
 * 
 * Electrical panels, transformers, and switchgear with:
 * - Panel Scheduling (Branch Circuit / Switchboard)
 * - Distribution System definitions (Voltage, Phases, Wires)
 * - Load classification and demand factors
 * - Circuit management
 */

import { v4 as uuidv4 } from 'uuid';
import { Element, Constraint } from '../core/types';
import { BaseTool } from './BaseTool';
import { ThreeEvent } from '@react-three/fiber';
import * as THREE from 'three';

// ==================== Equipment Types ====================

export type EquipmentCategory =
    | 'Panelboard'          // Breaker panel
    | 'Switchboard'         // Large distribution
    | 'Transformer'         // Voltage step-down
    | 'Switchgear'          // High voltage
    | 'Disconnect Switch'   // Safety switch
    | 'Motor Control Center'; // MCC

export type MountingType = 'Surface' | 'Recessed' | 'Floor';

export interface DistributionSystem {
    name: string;           // e.g., "120/208 Single"
    voltage: number;        // 120, 208, 277, 480
    phases: 1 | 3;
    wires: 3 | 4;           // incl. neutral
    configuration: 'Wye' | 'Delta';
}

// ==================== Circuit & Load Data ====================

export interface CircuitBranch {
    circuitNumber: string;  // "1", "2", "3,5,7"
    load: number;           // VA
    voltage: number;
    description: string;
    tripRating: number;     // Amps (15, 20, 30...)
}

export interface PanelScheduleData {
    mainsRating: number;    // Amps (100, 200, 400...)
    totalConnectedLoad: number; // VA
    totalDemandLoad: number;    // VA (after diversity)
    circuits: CircuitBranch[];
}

// ==================== Enhanced Geometry ====================

export interface EnhancedEquipmentGeometryData {
    // Spatial
    position: [number, number, number];
    rotation: number;

    // Type info
    category: EquipmentCategory;
    width: number;
    height: number;
    depth: number;
    mounting: MountingType;

    // Electrical
    distributionSystem: DistributionSystem;

    // Hosting
    hostId?: string;
    levelId: string;
    elevation: number;
}

// ==================== Enhanced Equipment Element ====================

export interface EnhancedEquipmentElement extends Element {
    category: 'Electrical Equipment';
    geometryData: EnhancedEquipmentGeometryData;

    // Logic
    panelData?: PanelScheduleData;

    // Connections
    feedThroughId?: string; // Downstream panel
}

// ==================== Tool Options ====================

export interface EquipmentToolOptions {
    category: EquipmentCategory;
    familyName: string;
    typeName: string;

    // Dimensions
    width: number;
    height: number;
    depth: number;
    mounting: MountingType;

    // Electrical
    voltage: number;
    phases: 1 | 3;
    mainsRating: number;

    // Placement
    levelId: string;
    elevation: number;
    autoHost: boolean;
}

// ==================== Electrical Equipment Tool Class ====================

export class ElectricalEquipmentTool extends BaseTool {
    id = 'electrical_equipment';
    name = 'Electrical Equipment';
    cursor = 'crosshair';

    private currentPoint: THREE.Vector3 | null = null;
    private currentHost: Element | null = null;
    private currentQuat: THREE.Quaternion = new THREE.Quaternion();

    private options: EquipmentToolOptions = this.getDefaultOptions();

    private getDefaultOptions(): EquipmentToolOptions {
        return {
            category: 'Panelboard',
            familyName: 'Lighting Panel',
            typeName: '208V MLO 225A',
            width: 508,     // 20"
            height: 1524,   // 60"
            depth: 146,     // 5.75"
            mounting: 'Surface',
            voltage: 208,
            phases: 3,
            mainsRating: 225,
            levelId: 'Level 1',
            elevation: 1200, // Centerline AFF
            autoHost: true
        };
    }

    /**
     * Set options from UI
     */
    setOptions(options: Partial<EquipmentToolOptions>): void {
        this.options = { ...this.options, ...options };
    }

    /**
     * Get current options
     */
    getOptions(): EquipmentToolOptions {
        return { ...this.options };
    }

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        if (event.button !== 0) return;

        const point = this.getIntersectionPoint(event);
        if (!point) return;

        this.createEquipment(point, this.currentHost, this.currentQuat);
    }

    onPointerMove(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;

        const point = this.getIntersectionPoint(event);

        if (point) {
            this.currentPoint = point;
            this.updatePreview();
        }
    }

    private getIntersectionPoint(event: ThreeEvent<PointerEvent>): THREE.Vector3 | null {
        if (!this.context) return null;

        if (this.options.autoHost) {
            // Check for Walls
            const hit = event.intersections.find(i => {
                const id = this.findId(i.object);
                if (!id) return false;
                const el = this.context!.bimStore.project.elements.get(id);
                return el && el.category === 'Walls';
            });

            if (hit) {
                const id = this.findId(hit.object)!;
                const el = this.context.bimStore.project.elements.get(id)!;
                this.currentHost = el;

                // Align to face normal
                const normal = hit.face?.normal || new THREE.Vector3(0, -1, 0);
                const lookAt = new THREE.Vector3().copy(hit.point).add(normal);
                this.currentQuat.setFromRotationMatrix(
                    new THREE.Matrix4().lookAt(lookAt, hit.point, new THREE.Vector3(0, 0, 1))
                );

                // For equipment, maintain centerline elevation
                return new THREE.Vector3(hit.point.x, hit.point.y, this.options.elevation / 1000);
            }
        }

        // Fallback to floor
        this.currentHost = null;
        this.currentQuat.set(0, 0, 0, 1);

        const planeHit = this.getIntersection(event);
        if (planeHit) {
            const point = this.snapToGrid(planeHit);
            // If floor mounted, sit on floor. If wall mounted (but no wall), float.
            if (this.options.mounting === 'Floor') {
                point.z = 0;
            } else {
                point.z = this.options.elevation / 1000;
            }
            return point;
        }

        return null;
    }

    onPointerUp(): void { }

    private updatePreview() {
        if (!this.context) return;

        if (this.currentPoint) {
            this.context.appStore.setToolPreview({
                type: 'cube', // Generic cube for equipment
                position: this.currentPoint,
                rotation: new THREE.Euler().setFromQuaternion(this.currentQuat),
                dimensions: [this.options.width / 1000, this.options.depth / 1000, this.options.height / 1000],
                color: '#666666'
            });
        } else {
            this.context.appStore.setToolPreview(null);
        }
    }

    private createEquipment(point: THREE.Vector3, host: Element | null, rotation: THREE.Quaternion) {
        if (!this.context) return;

        const euler = new THREE.Euler().setFromQuaternion(rotation);

        const distributionSystem: DistributionSystem = {
            name: `${this.options.voltage}V ${this.options.phases}Ø`,
            voltage: this.options.voltage,
            phases: this.options.phases,
            wires: this.options.phases === 3 ? 4 : 3, // Simplistic assumption
            configuration: 'Wye'
        };

        const geometryData: EnhancedEquipmentGeometryData = {
            position: point.toArray() as [number, number, number],
            rotation: euler.z * (180 / Math.PI),
            category: this.options.category,
            width: this.options.width,
            height: this.options.height,
            depth: this.options.depth,
            mounting: this.options.mounting,
            distributionSystem,
            hostId: host?.id,
            levelId: this.options.levelId,
            elevation: this.options.elevation
        };

        const panelData: PanelScheduleData = {
            mainsRating: this.options.mainsRating,
            totalConnectedLoad: 0,
            totalDemandLoad: 0,
            circuits: [] // Empty schedule initially
        };

        const equipment: EnhancedEquipmentElement = {
            id: uuidv4(),
            category: 'Electrical Equipment',
            type: `${this.options.familyName}: ${this.options.typeName}`,
            parameters: this.buildParameters(geometryData),
            constraints: this.getDefaultConstraints(host),
            geometryData,
            panelData
        };

        this.context.bimStore.addElement(equipment);
    }

    private buildParameters(geom: EnhancedEquipmentGeometryData): Record<string, any> {
        return {
            'Dimensions': `${geom.width}x${geom.height}x${geom.depth}`,
            'Mounting': geom.mounting,
            'Voltage': `${geom.distributionSystem.voltage}V`,
            'Phases': geom.distributionSystem.phases,
            'Mains Rating': `${this.options.mainsRating} A`,
            'Elevation': geom.elevation,
            'Host': geom.hostId || '<None>'
        };
    }

    private getDefaultConstraints(host: Element | null): Constraint[] {
        const constraints: Constraint[] = [
            {
                type: 'LEVEL_BASED',
                targetId: this.options.levelId,
                data: { offset: this.options.elevation }
            }
        ];

        if (host) {
            constraints.push({
                type: 'HOST_CONTAINED',
                targetId: host.id,
                data: {}
            });
        }

        return constraints;
    }
}

// Export types

